export const transformEducationPeriod = (educationPeriods) =>
  educationPeriods.map(({ id, displayValue }) => ({
    label: displayValue,
    value: id,
  }));

export const transformSchoolSet = (
  schoolInitialState,
  extractedDistrictSchoolStudentHierarchyData,
  districtSelected,
) => {
  const schoolSet =
    extractedDistrictSchoolStudentHierarchyData &&
    Object.keys(extractedDistrictSchoolStudentHierarchyData).length &&
    districtSelected &&
    extractedDistrictSchoolStudentHierarchyData[districtSelected.value]
      ? Object.values(
          extractedDistrictSchoolStudentHierarchyData[districtSelected.value]
            .schools,
        )
      : [];
  return [
    schoolInitialState,
    ...schoolSet
      .filter(({ sections }) => Object.keys(sections).length >= 1) // filter out schools with no sections
      .map(({ id, name }) => ({ label: name, value: id })),
  ];
};

export const tagsWithAssessmentKeys = (tags) => {
  return tags.map(({ assessments, ...otherProps }) => ({
    assessments,
    ...otherProps,
    ...Object.values(assessments).reduce(
      (acc, { id, percentage }) => ({
        ...acc,
        [`assessment_${id}`]: percentage,
      }),
      {},
    ),
  }));
};

const extractQuestionsIds = (questionIds) => {
  let questionIdsString = '';
  questionIds.forEach((questionId) => {
    questionIdsString += `${questionId},`;
  });
  return questionIdsString;
};

export const tagsFromData = (data) => {
  return data.map((tag) => {
    let questionByTagData = {};
    const assessmentData = tag.assessments.map((assessment) => {
      const scores = assessment.questions.reduce(
        (acc, question) => {
          questionByTagData = generateQuestionsByTagData(
            questionByTagData,
            question,
          );
          acc.score += question.score;
          acc.maxScore += question.maxScore;
          return acc;
        },
        { score: 0, maxScore: 0, totalStudents: 0 },
      );
      const questions = assessment.questions.map(({ itemId }) => itemId);
      return {
        id: assessment.id,
        title: assessment.title,
        type: assessment.type,
        questions,
        totalQuestions: new Set(questions).size,
        percentage: Math.round((scores.score * 100) / scores.maxScore),
        ...scores,
      };
    });

    const tagTotals = assessmentData.reduce(
      (acc, assessment) => {
        acc.score += assessment.score;
        acc.maxScore += assessment.maxScore;
        acc.questions = [...assessment.questions, ...acc.questions];
        acc.assessments[assessment.id] = assessment;
        return acc;
      },
      {
        score: 0,
        maxScore: 0,
        totalStudents: 0,
        questions: [],
        assessments: {},
      },
    );
    const questionIds = Object.keys(questionByTagData);
    return {
      id: tag.id,
      label: tag.label,
      description: tag.description,
      studentsScored: tag.numberOfStudentsScored.hasOwnProperty('all')
        ? tag.numberOfStudentsScored['all']
        : tag.numberOfStudentsScored,
      totalQuestions: new Set(tagTotals.questions).size,
      percentage: Math.round((tagTotals.score * 100) / tagTotals.maxScore),
      assessments: tagTotals.assessments,
      questionByTagData: Object.values(questionByTagData),
      questionIdsInString: extractQuestionsIds(questionIds),
    };
  });
};

export const generateQuestionsByTagData = (questionByTagData, question) => {
  const {
    itemId: id,
    title,
    score,
    maxScore,
    learnosityItemReference,
  } = question;
  const questionInfo = questionByTagData[id] || {
    totalScore: 0,
    totalMaxScore: 0,
    title,
    itemId: id,
    learnosityItemReference,
  };
  questionInfo.totalScore += score;
  questionInfo.totalMaxScore += maxScore;
  questionInfo.avgScorePercentage = Math.round(
    (questionInfo.totalScore * 100) / questionInfo.totalMaxScore,
  );
  return { ...questionByTagData, [id]: questionInfo };
};

export const getAssessments = (data) => {
  let firstDate = '';
  let lastDate = '';
  let assessments = Object.values(
    data.reduce((acc, tag) => {
      tag.assessments.forEach((assessment) => {
        if (!acc.hasOwnProperty(assessment.id)) {
          acc[assessment.id] = {
            id: assessment.id,
            title: assessment.title,
          };
        }
        const { firstDateScored, lastDateScored } =
          assessment.assignments.reduce(
            (acc, assignment) => {
              if (
                acc.firstDateScored === null ||
                assignment.firstDateScored < acc.firstDateScored
              ) {
                acc.firstDateScored = assignment.firstDateScored;
              }
              if (
                acc.lastDateScored === null ||
                assignment.lastDateScored > acc.lastDateScored
              ) {
                acc.lastDateScored = assignment.lastDateScored;
              }
              return acc;
            },
            { firstDateScored: null, lastDateScored: null },
          );

        if (acc[assessment.id].firstDateScored === undefined) {
          acc[assessment.id].firstDateScored = firstDateScored;
          acc[assessment.id].lastDateScored = lastDateScored;
        } else {
          if (firstDateScored < acc[assessment.id].firstDateScored) {
            acc[assessment.id].firstDateScored = firstDateScored;
          }
          if (lastDateScored > acc[assessment.id].lastDateScored) {
            acc[assessment.id].lastDateScored = lastDateScored;
          }
        }

        if (firstDate === '' || firstDateScored < firstDate) {
          firstDate = firstDateScored;
        }

        if (lastDate === '' || lastDateScored > lastDate) {
          lastDate = lastDateScored;
        }
      });
      return acc;
    }, {}),
  ).sort((a, b) => (a.firstDateScored < b.firstDateScored ? 1 : -1));
  return { assessments, firstDate, lastDate };
};

export const getRequiredEducationPeriods = (
  currentEducationPeriod,
  educationPeriods,
  earliestReportingEP = 0,
) => {
  // the content & skill report was created in EP 24
  // so we should only show EPs from 24 onwards
  const initialEducationPeriod = Math.max(24, earliestReportingEP);
  return educationPeriods.filter(
    ({ id }) => id >= initialEducationPeriod && id <= currentEducationPeriod,
  );
};

export const transformDistrictData = (districtSchoolStudentHierarchyData) =>
  Object.values(districtSchoolStudentHierarchyData || {}).map(
    ({ name, id }) => ({
      label: name,
      value: id,
    }),
  );

export const extractDistrictSchoolStudentHierarchyData = (
  districtSchoolStudentHierarchyData,
  selectedDistrictId,
) => {
  if (!selectedDistrictId) {
    return districtSchoolStudentHierarchyData;
  }
  return {
    [selectedDistrictId]:
      districtSchoolStudentHierarchyData[selectedDistrictId],
  };
};

export const extractSchoolData = (currentDistrictSchoolStudentHierarchyData) =>
  Object.values(currentDistrictSchoolStudentHierarchyData || {})
    .filter((districtData) => districtData)
    .flatMap((districtData) => Object.values(districtData.schools));

export const extractClassData = (schoolData) =>
  Object.values(schoolData || {})
    .filter((school) => school)
    .flatMap((school) => Object.values(school.sections));

export const extractStudentsData = (classData) => {
  let studentsData = [];
  Object.values(classData || {}).forEach((section) => {
    if (section) {
      studentsData = [...studentsData, ...Object.values(section?.students)];
    }
  });
  return studentsData;
};

export const transFormClassData = (
  sectionInitialState,
  districtSchoolStudentHierarchyData,
  schoolSelected,
) => {
  const schoolData = extractSchoolData(districtSchoolStudentHierarchyData);
  const classData = extractClassData(
    schoolSelected.value !== null
      ? schoolData.filter(({ id }) => id === Number(schoolSelected.value))
      : schoolData,
  );
  const transFormedClassData = classData
    .map(({ id, name }) => ({
      label: name,
      value: id,
    }))
    .sort((a, b) => (a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1));
  return [sectionInitialState, ...transFormedClassData];
};

export const transFormStudentsData = (
  studentsInitialState,
  districtSchoolStudentHierarchyData,
  selectedClassId,
) => {
  const schoolData = extractSchoolData(districtSchoolStudentHierarchyData);
  const classData = extractClassData(schoolData).filter(
    ({ id }) => !selectedClassId || id === selectedClassId,
  );
  let studentsData = extractStudentsData(classData);
  studentsData = studentsData
    .map(({ id, first_name: firstName, last_name: lastName }) => ({
      label: `${firstName} ${lastName}`,
      value: id,
    }))
    .sort((a, b) => (a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1));
  return [studentsInitialState, ...studentsData];
};

export const transformCategoryData = (categories) => {
  if (!categories) {
    return [];
  }

  return categories
    .slice()
    .sort((cat1, cat2) => (cat1.title > cat2.title ? 1 : -1))
    .map((cat) => ({ label: cat.title, value: cat.id }));
};

export const getPeriodStartDate = (dateToBeFormatted) => {
  let dateFromData = new Date(dateToBeFormatted);
  dateFromData = dateFromData.setMonth(dateFromData.getMonth() + 1);
  return dateFromData;
};

export class TrendDataFilterHelper {
  static PPC = 'ppc';
  static TEACHER_AUTHORED = 'qb';
  static PE = 'pe';

  static ALL_TAB = 'All';
  static PROGRESS_CHECKS_TAB = 'Progress Checks';
  static MY_QUIZZES_TAB = 'My Quizzes';
  static PRACTICE_EXAMS_TAB = 'Practice Exams';
  static TYPE_FILTERS = [
    {
      name: TrendDataFilterHelper.ALL_TAB,
      value: 'all',
    },
    {
      name: TrendDataFilterHelper.PROGRESS_CHECKS_TAB,
      value: TrendDataFilterHelper.PPC,
    },
    {
      name: TrendDataFilterHelper.MY_QUIZZES_TAB,
      value: TrendDataFilterHelper.TEACHER_AUTHORED,
    },
    {
      name: TrendDataFilterHelper.PRACTICE_EXAMS_TAB,
      value: TrendDataFilterHelper.PE,
    },
  ];
  static ASSESSMENT_TYPE_MAP = {
    all: '',
    ppc: 'Progress Check',
    pe: 'Practice Exam',
    qb: 'Quiz',
  };

  static filterByAssessmentType(trendData, selectedAssessmentType) {
    const assessmentTypeValue =
      TrendDataFilterHelper.ASSESSMENT_TYPE_MAP[selectedAssessmentType];
    if (!assessmentTypeValue) {
      return trendData;
    }
    return trendData.map((course) => {
      const filterdData = {
        ...course,
        assessments: course.assessments.filter(
          (assessment) => assessment.assessmentType === assessmentTypeValue,
        ),
      };
      const numberOfStudentsScored =
        filterdData.assessments.length === 0
          ? 0
          : filterdData.numberOfStudentsScored[selectedAssessmentType];
      return {
        ...filterdData,
        numberOfStudentsScored,
      };
    });
  }
}

export const transformQuestionsTags = (questions) => {
  const labelNamesToCamelCaseMap = {
    Skill: 'skills',
    Unit: 'units',
    Topic: 'topics',
    'Big Idea': 'bigIdeas',
    'Task Model': 'taskModels',
    Proficiencies: 'proficiencies',
    'Course Transfer Skills': 'courseTransferSkills',
  };

  const transformedTags = {};

  questions.forEach((question) => {
    question.tags.forEach((tag) => {
      const category = tag.categories.find((category) => {
        return category.label in labelNamesToCamelCaseMap;
      });

      if (category) {
        const categoryName = labelNamesToCamelCaseMap[category.label];
        if (!transformedTags[categoryName]) {
          transformedTags[categoryName] = [];
        }

        const existingTag = transformedTags[categoryName].find(
          (existingTag) => {
            return existingTag.label === tag.label;
          },
        );

        if (!existingTag) {
          transformedTags[categoryName].push({
            label: tag.label,
            description: tag.description || '',
          });
        }
      }
    });
  });

  return transformedTags;
};

export const getQuestionMetaData = (questions) => {
  return questions.reduce(
    (acc, question) => {
      acc.numberOfMcq +=
        question.__typename === 'MultipleChoiceQuestion' ? 1 : 0;
      acc.numberOfFrq += question.__typename === 'FreeResponseQuestion' ? 1 : 0;
      acc.numberOfTeacherQuestions += question.isTeacherAuthored ? 1 : 0;
      acc.itemIds.push(question.itemId);
      return acc;
    },
    {
      numberOfMcq: 0,
      numberOfFrq: 0,
      numberOfQuestions: questions.length,
      numberOfTeacherQuestions: 0,
      itemIds: [],
    },
  );
};

export const getAssignmentsIdsFromData = (data) =>
  data.flatMap((tag) =>
    tag.assessments.flatMap((assessment) =>
      assessment.assignments.map((assignment) => assignment.id),
    ),
  );
